<template>
  <div class="container-fluid">
    <!-- 移动端顶部栏（仅小屏显示） -->
    <div class="mobile-topbar d-md-none d-flex align-items-center justify-content-between">
      <div class="brand-inline d-flex align-items-center ms-2">
        <img class="brand-logo" src="/icon-192x192.png" alt="logo">
        <span class="brand-title">通行线报站编排工具</span>
      </div>
      <button class="btn btn-outline-secondary me-2 hamburger-btn" @click="isSidebarOpen = !isSidebarOpen" :aria-label="isSidebarOpen ? '收起菜单' : '展开菜单'">
        <!-- lucide chevron icons -->
        <svg v-if="!isSidebarOpen" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="m6 9 6 6 6-6"/>
        </svg>
        <svg v-else xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="m18 15-6-6-6 6"/>
        </svg>
      </button>
    </div>
    <!-- 遮罩层（移动端侧栏打开时显示） -->
    <div v-if="isSidebarOpen" class="mobile-overlay d-md-none" @click="isSidebarOpen = false"></div>
    <div class="row">
      <!-- 左侧导航栏 -->
      <div class="col-md-2 sidebar py-3 d-none d-md-block" :class="{ 'mobile-open': isSidebarOpen }">
        <!-- 品牌区 -->
        <div class="brand-header d-flex align-items-center">
          <img class="brand-logo" src="/icon-192x192.png" alt="logo">
          <span class="brand-title">通行线报站编排工具</span>
        </div>
        <div class="nav flex-column nav-pills" role="tablist">
          <button class="nav-link active mb-2" 
                  id="line-station-tab" 
                  data-bs-toggle="pill" 
                  data-bs-target="#line-station" 
                  type="button" 
                  role="tab"
                  @click="closeSidebarOnMobile">
            <span class="menu-text">线路与站点</span>
          </button>
          <button class="nav-link mb-2" 
                  id="template-config-tab" 
                  data-bs-toggle="pill" 
                  data-bs-target="#template-config" 
                  type="button" 
                  role="tab"
                  @click="closeSidebarOnMobile">
            <span class="menu-text">站点个性化</span>
          </button>
          <button class="nav-link mb-2" 
                  id="led-control-tab" 
                  data-bs-toggle="pill" 
                  data-bs-target="#led-control" 
                  type="button" 
                  role="tab"
                  @click="closeSidebarOnMobile">
            <span class="menu-text">LED设置</span>
          </button>
          
          <hr class="my-3 border-light">
          
          <!-- 选择音频文件夹按钮 -->
          <div class="mb-2">
            <button class="btn btn-outline-primary w-100" @click="openAudioFolderModal(); closeSidebarOnMobile()">
              <i class="bi bi-folder me-2"></i><span class="menu-text">选择音频文件夹</span>
            </button>
          </div>
          
          <!-- 导入JSON按钮 -->
          <div class="mb-2">
            <input type="file" id="importJsonFile" accept=".json,.ini" style="display: none;" @change="importJsonFileHandler">
            <button class="btn btn-primary w-100" @click="triggerJsonFileInput(); closeSidebarOnMobile()">
              <i class="bi bi-upload me-2"></i><span class="menu-text">导入JSON配置</span>
            </button>
          </div>
          
          <!-- 下载JSON按钮 -->
          <div class="mb-2">
            <button class="btn btn-success w-100" @click="downloadJsonFile(); closeSidebarOnMobile()">
              <i class="bi bi-download me-2"></i><span class="menu-text">下载JSON配置</span>
            </button>
          </div>

          <!-- 添加下载压缩包按钮 -->
          <div class="mb-2">
            <button class="btn btn-info w-100" @click="downloadZipPackage(); closeSidebarOnMobile()">
              <i class="bi bi-file-earmark-zip me-2"></i><span class="menu-text">下载线路压缩包</span>
            </button>
          </div>

          <!-- 清除缓存按钮 -->
          <div>
            <button class="btn btn-warning w-100" @click="clearAllCache(); closeSidebarOnMobile()">
              <i class="bi bi-trash me-2"></i><span class="menu-text">清除缓存数据</span>
            </button>
          </div>
        </div>

        <!-- 隐私政策链接（置于侧边栏底部） -->
        <div class="mt-3 text-center">
          <a href="javascript:void(0)" class="text-light" style="opacity: .7; font-size: 12px;" @click="showPrivacy = true">© 2025 通行线 · 隐私政策 · V1.0.1</a>
        </div>
      </div>

      <!-- 右侧内容区 -->
      <div class="col-md-10 main-content py-3">
        <div class="tab-content">
          <!-- 线路与站点合并面板 -->
          <div class="tab-pane fade show active" id="line-station" role="tabpanel">
            <div class="row">
              <!-- 左侧：线路信息 -->
              <div class="col-md-4">
                <StationListPanel 
                  :upDirectionStations="upDirectionStations" 
                  :downDirectionStations="downDirectionStations"
                  :currentDirection="currentDirection"
                  :selectedStationIndex="selectedStationIndex"
                  :languageSettings="languageSettings"
                  :lineName="lineName"
                  :lineType="lineType"
                  @directionChanged="switchDirection"
                  @selectStation="selectStation"
                  @editStation="openStationEditModal"
                  @importStations="importStations"
                  @addStation="addStation"
                  @removeStation="removeStation"
                  @clearStations="clearStations"
                  @moveStation="moveStation"
                  @updateLanguageSettings="updateLanguageSettings"
                  @updateLineName="updateLineName"
                  @lineTypeChanged="updateLineType"
                  @importSuccess="handleImportSuccess"
                  @reverseSuccess="handleReverseSuccess"
                  @bulkUpdateStationNames="handleBulkUpdateStationNames"
                  ref="stationListPanel"
                />
              </div>
              
              <!-- 右侧：报站模板配置 -->
              <div class="col-md-8">
                <AudioTemplatePanel 
                  :audioFolders="audioFolders"
                  :languageSettings="languageSettings"
                  :currentDirection="currentDirection"
                  :defaultArrivalAudioSequence="defaultArrivalAudioSequence"
                  :defaultDepartureAudioSequence="defaultDepartureAudioSequence"
                  :welcomeAudioSequence="welcomeAudioSequence"
                  :terminalAudioSequence="terminalAudioSequence"
                  :upArrivalAudioSequence="upArrivalAudioSequence"
                  :downArrivalAudioSequence="downArrivalAudioSequence"
                  :upDepartureAudioSequence="upDepartureAudioSequence"
                  :downDepartureAudioSequence="downDepartureAudioSequence"
                  :upWelcomeAudioSequence="upWelcomeAudioSequence"
                  :downWelcomeAudioSequence="downWelcomeAudioSequence"
                  :upTerminalAudioSequence="upTerminalAudioSequence"
                  :downTerminalAudioSequence="downTerminalAudioSequence"
                  :serviceAudioSequences="serviceAudioSequences"
                  :separateDirections="separateDirections"
                  :upDirectionStations="upDirectionStations"
                  :downDirectionStations="downDirectionStations"
                  :selectedStationIndex="selectedStationIndex"
                  :lineType="lineType"
                  :lineName="lineName"
                  @audioConfigChanged="handleAudioConfigChanged"
                  ref="audioTemplatePanel"
                />
              </div>
            </div>
          </div>

          <!-- 站点个性化面板 -->
          <div class="tab-pane fade" id="template-config" role="tabpanel">
            <div class="row">
              <!-- 左侧：站点选择 -->
              <div class="col-md-4">
                <div class="card">
                  <div class="card-header bg-info text-white">
                    <h5 class="mb-0">站点选择</h5>
                  </div>
                  <div class="card-body">
                    <!-- 方向选择 -->
                    <div class="btn-group d-flex justify-content-center mb-3" role="group" v-if="lineType === 'twoWay'">
                      <button type="button" class="btn btn-sm btn-outline-primary" :class="{ 'active': currentDirection === 'up' }" @click="switchDirection('up')">上行</button>
                      <button type="button" class="btn btn-sm btn-outline-primary" :class="{ 'active': currentDirection === 'down' }" @click="switchDirection('down')">下行</button>
                    </div>

                                         <!-- 站点列表 -->
                     <div class="station-list" id="stationListPersonalized">
                       <div v-if="!currentStations.length" class="text-center text-muted py-5">
                         请先创建站名列表
                       </div>
                       <div v-else>
                         <div 
                           v-for="(station, index) in currentStations" 
                           :key="station.id" 
                           class="station-item" 
                           :class="{ 
                             'active': selectedStationIndex === index,
                             'drag-over': draggedStationIndex !== null && draggedStationIndex !== index && stationDropIndicatorIndex === index
                           }"
                           draggable="true"
                           @dragstart="onStationDragStart($event, index)"
                           @dragover="onStationDragOver"
                           @dragenter="onStationDragEnter"
                           @dragleave="onStationDragLeave"
                           @drop="onStationDrop($event, index)"
                           @click="selectStation(index)"
                         >
                           <div class="d-flex align-items-center">
                             <span class="drag-handle me-2">≡</span>
                             <span class="badge bg-secondary me-2">{{ index + 1 }}</span>
                             <span class="flex-grow-1">{{ getStationDisplayName(station) }}</span>
                             <span v-if="index === 0" class="badge bg-success badge-station-type">起点站</span>
                             <span v-else-if="index === currentStations.length - 1" class="badge bg-danger badge-station-type">终点站</span>
                           </div>
                         </div>
                         <!-- 拖拽辅助线 -->
                         <div 
                           v-if="stationDropIndicatorIndex !== null && stationDropIndicatorIndex === currentStations.length" 
                           class="drop-indicator"
                         ></div>
                       </div>
                     </div>
                  </div>
                </div>
              </div>
              
              <!-- 右侧：站点配置 -->
              <div class="col-md-8">
                <StationConfigPanel 
                  :selectedStation="selectedStation" 
                  :stationIndex="selectedStationIndex"
                  :totalStations="currentStations.length"
                  :audioFolders="audioFolders"
                  :languageSettings="languageSettings"
                  :hideAdvancedSettings="hideAdvancedSettings"
                  :ledSettings="ledSettings"
                  :upDirectionStations="upDirectionStations"
                  :downDirectionStations="downDirectionStations"
                  :currentDirection="currentDirection"
                  @editStation="openStationEditModal"
                />
              </div>
            </div>
          </div>

          <!-- LED设置面板 -->
          <div class="tab-pane fade" id="led-control" role="tabpanel">
            <LEDControlPanel 
              :ledSettings="ledSettings"
              :languageSettings="languageSettings"
              @update:ledSettings="updateLEDSettings"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- 模态框 -->
    <StationEditModal 
      :station="editingStation"
      :stations="currentStations"
      :audioFolders="audioFolders"
      :languageSettings="languageSettings"
      :hideAdvancedSettings="hideAdvancedSettings"
      :defaultArrivalAudioSequence="defaultArrivalAudioSequence"
      :defaultDepartureAudioSequence="defaultDepartureAudioSequence"
      :upArrivalAudioSequence="upArrivalAudioSequence"
      :downArrivalAudioSequence="downArrivalAudioSequence"
      :upDepartureAudioSequence="upDepartureAudioSequence"
      :downDepartureAudioSequence="downDepartureAudioSequence"
      :separateDirections="separateDirections"
      :currentDirection="currentDirection"
      @save="saveStationEdit"
      ref="stationEditModal"
    />

    <AudioFolderModal 
      @folderSelected="handleFolderSelected"
      ref="audioFolderModal"
    />

    <div class="modal fade" id="zipConfigModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">检测到压缩包中的配置</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close" @click="hideZipConfigModal"></button>
          </div>
          <div class="modal-body">
            <p class="mb-3">当前压缩包内检测到以下 JSON 配置文件，可选择是否应用：</p>
            <div v-if="pendingZipConfigs.length > 0" class="list-group">
              <label v-for="(config, index) in pendingZipConfigs" :key="config.entryPath" class="list-group-item list-group-item-action d-flex align-items-center">
                <input class="form-check-input me-2" type="radio" name="zipConfigRadio" :value="index" v-model="selectedZipConfigIndex">
                <div>
                  <div class="fw-semibold">{{ config.name }}</div>
                  <small class="text-muted">路径：{{ config.path }}</small>
                </div>
              </label>
            </div>
            <div v-else class="alert alert-info">
              未检测到可用的配置文件。
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="hideZipConfigModal">取消</button>
            <button type="button" class="btn btn-primary" :disabled="pendingZipConfigs.length === 0 || isApplyingZipConfig" @click="applyZipConfig">
              {{ isApplyingZipConfig ? '应用中...' : '确定' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 隐私政策模态框 -->
    <Teleport to="body">
      <div v-if="showPrivacy" class="privacy-modal" style="display: block;">
        <div class="privacy-modal-content">
          <button class="privacy-close" @click="showPrivacy = false">×</button>
          <h5 class="mb-2">隐私政策与使用条款（通行线报站配置工具）</h5>
          <p class="text-muted mb-2" style="font-size: 12px;">重要提示：本工具所有数据仅在您的浏览器本地存储与处理，不会上传到任何云端或服务器。</p>
          <div class="privacy-body">
            <p>欢迎使用通行线报站配置工具。使用本工具即表示您接受以下条款和条件。如果您不同意这些条款，请勿使用本工具。</p>
            <p><strong>1. 软件服务</strong><br>
            本工具面向已购买通行线像素相关硬件产品的用户，用于站点管理、语音模板编辑、LED显示内容配置以及生成适用于通行线报站机的配置压缩包。</p>
            <p><strong>2. 用户内容责任声明</strong><br>
            您通过本工具创建的所有内容均由您自行负责。通行线不参与、不干预您的内容创作过程，也不对您所创建内容的合法性、准确性或适当性负责。使用本工具创建、编辑和导出的所有内容及其可能产生的任何后果，均由您自行承担。</p>
            <p><strong>3. 用户数据与隐私</strong><br>
            本工具使用浏览器本地存储功能保存您的线路配置、音频引用及显示设置。所有数据均保存在您的浏览器中，仅在本地访问与处理，不会上传至任何服务器或云端。清空浏览器缓存将导致数据丢失，请定期导出配置文件作为备份。</p>
            <p><strong>4. 知识产权</strong><br>
            您创建的内容归您所有，您对自己创建的内容拥有完全的知识产权和使用权。本工具不会对您创建的设计主张任何权利。工具本身及其源代码的知识产权归通行线所有。</p>
            <p><strong>5. 使用限制</strong><br>
            禁止使用本工具创建违法、侵权或不适当的内容。若您违反相关法律法规，所有法律责任由您自行承担。禁止尝试反编译、破解或修改本工具的源代码，除非获得明确授权。</p>
            <p><strong>6. 免责声明</strong><br>
            本工具按“现状”提供，不对其功能完整性、可靠性或适用性做任何明示或暗示的保证。使用本工具所产生的任何后果由用户自行承担。通行线不对因使用本工具而导致的任何直接或间接损失负责。</p>
            <p><strong>7. 协议更新</strong><br>
            我们保留随时修改本协议的权利，继续使用本工具将视为接受修改后的协议。</p>
            <p><strong>8. 意见建议</strong><br>
            如对本工具或协议有任何疑问或建议，请联系通行线团队。</p>
          </div>
          <div class="text-end mt-2">
            <button class="btn btn-sm btn-secondary" @click="showPrivacy = false">关闭</button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import StationListPanel from './components/StationListPanel.vue';
import StationConfigPanel from './components/StationConfigPanel.vue';
import TemplatePanel from './components/TemplatePanel.vue';
import AudioTemplatePanel from './components/AudioTemplatePanel.vue';
import StationEditModal from './components/StationEditModal.vue';
import AudioFolderModal from './components/AudioFolderModal.vue';
import LEDControlPanel from './components/LEDControlPanel.vue';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import cacheManager from './utils/cache.js';
// 确保Bootstrap正确导入
import * as bootstrap from 'bootstrap';

// 使Bootstrap全局可用
window.bootstrap = bootstrap;

export default {
  name: 'App',
  components: {
    StationListPanel,
    StationConfigPanel,
    TemplatePanel,
    AudioTemplatePanel,
    StationEditModal,
    AudioFolderModal,
    LEDControlPanel
  },
      data() {
    return {
      upDirectionStations: [],
      downDirectionStations: [],
      currentDirection: 'up',
      selectedStationIndex: -1,
      editingStation: null,
      audioFolders: [],  // 存储所有音频子文件夹
      arrivalTemplate: '【当前站】到了',
      departureTemplate: '前方到站，【当前站】，请准备下车',
      generatedJson: null,
      projectFolder: null,
      projectFolderPath: '',  // 存储项目文件夹的实际路径
      // 新增：用以兼容 ZIP 来源
      sourceType: null, // 'directory' | 'zip'
      zipEntries: null,
      zipAudioPaths: [],
      zipFileName: '',
      pendingZipConfigs: [],
      selectedZipConfigIndex: 0,
      zipConfigModal: null,
      isApplyingZipConfig: false,
      defaultArrivalAudioSequence: [],
      defaultDepartureAudioSequence: [],
      welcomeAudioSequence: [], // 欢迎语音频序列
      terminalAudioSequence: [], // 终点站语音频序列
      // 上下行分别的音频序列
      upArrivalAudioSequence: [],
      downArrivalAudioSequence: [],
      upDepartureAudioSequence: [],
      downDepartureAudioSequence: [],
      upWelcomeAudioSequence: [],
      downWelcomeAudioSequence: [],
      upTerminalAudioSequence: [],
      downTerminalAudioSequence: [],
      // 上下行不同设置
      separateDirections: {
        arrival: false,
        departure: false,
        welcome: false,
        terminal: false
      },
      serviceAudioSequences: Array(10).fill().map(() => []), // 10个服务语音频序列
      hideAdvancedSettings: false,  // 隐藏高级设置
      lineName: '', // 添加线路名称数据
      lineType: 'twoWay', // 添加线路类型数据
      // 添加全局多语言设置
      languageSettings: {
        enabled: false,
        count: 2,
        languages: [
          { name: '中文', code: 'zh' },
          { name: '英文', code: 'en' },
          { name: '日文', code: 'ja' },
          { name: '韩文', code: 'ko' },
          { name: '俄文', code: 'ru' }
        ]
      },
      ledSettings: {
        stationTextColor: '255',
        serviceTextColor: '65280',
        stationScrollMode: 'left',
        serviceScrollMode: 'left',
        stationNoScrollMode: 'left',
        serviceNoScrollMode: 'left',
        leftScrollSpeed: 10,
        upScrollSpeed: 5,
        // 多语言LED内容 - 主语言
        arrivalLEDContent: '【当前站】 到了',
        departureLEDContent: '下一站 【当前站】',
        welcomeLEDContent: '',
        service1LEDContent: '',
        service2LEDContent: '',
        service3LEDContent: '',
        service4LEDContent: '',
        service5LEDContent: '',
        service6LEDContent: '',
        service7LEDContent: '',
        service8LEDContent: '',
        service9LEDContent: '',
        service10LEDContent: '',
        // 多语言LED内容 - 其他语言
        arrivalLEDContent_en: '',
        departureLEDContent_en: '',
        welcomeLEDContent_en: '',
        service1LEDContent_en: '',
        service2LEDContent_en: '',
        service3LEDContent_en: '',
        service4LEDContent_en: '',
        service5LEDContent_en: '',
        service6LEDContent_en: '',
        service7LEDContent_en: '',
        service8LEDContent_en: '',
        service9LEDContent_en: '',
        service10LEDContent_en: '',
        arrivalLEDContent_ja: '',
        departureLEDContent_ja: '',
        welcomeLEDContent_ja: '',
        service1LEDContent_ja: '',
        service2LEDContent_ja: '',
        service3LEDContent_ja: '',
        service4LEDContent_ja: '',
        service5LEDContent_ja: '',
        service6LEDContent_ja: '',
        service7LEDContent_ja: '',
        service8LEDContent_ja: '',
        service9LEDContent_ja: '',
        service10LEDContent_ja: '',
        arrivalLEDContent_ko: '',
        departureLEDContent_ko: '',
        welcomeLEDContent_ko: '',
        service1LEDContent_ko: '',
        service2LEDContent_ko: '',
        service3LEDContent_ko: '',
        service4LEDContent_ko: '',
        service5LEDContent_ko: '',
        service6LEDContent_ko: '',
        service7LEDContent_ko: '',
        service8LEDContent_ko: '',
        service9LEDContent_ko: '',
        service10LEDContent_ko: '',
        arrivalLEDContent_ru: '',
        departureLEDContent_ru: '',
        welcomeLEDContent_ru: '',
        service1LEDContent_ru: '',
        service2LEDContent_ru: '',
        service3LEDContent_ru: '',
        service4LEDContent_ru: '',
        service5LEDContent_ru: '',
        service6LEDContent_ru: '',
        service7LEDContent_ru: '',
        service8LEDContent_ru: '',
        service9LEDContent_ru: '',
        service10LEDContent_ru: ''
      },
      draggedStationIndex: null,  // 站点拖拽排序用
      stationDropIndicatorIndex: null,  // 站点拖拽辅助线
      showPrivacy: false,
      // 移动端侧栏开关（两种状态：关闭/打开抽屉）
      isSidebarOpen: false
    };
  },
  computed: {
    currentStations() {
      return this.currentDirection === 'up' ? this.upDirectionStations : this.downDirectionStations;
    },
    selectedStation() {
      if (this.selectedStationIndex >= 0 && this.selectedStationIndex < this.currentStations.length) {
        return this.currentStations[this.selectedStationIndex];
      }
      return null;
    }
  },
  watch: {
    isSidebarOpen(newVal) {
      try {
        const body = document.body;
        if (newVal) {
          body.classList.add('body--locked');
        } else {
          body.classList.remove('body--locked');
        }
      } catch (e) {}
    }
  },
  mounted() {
    // 加载缓存数据
    this.loadCachedData();
    
    // 添加键盘快捷键用于调试 (Ctrl+D)
    document.addEventListener('keydown', (e) => {
      if (e.ctrlKey && e.key === 'd') {
        e.preventDefault();
        console.log('手动触发验证和强制检测...');
        this.forceDetectSeparateDirections();
        this.syncSeparateDirectionsToComponent();
        setTimeout(() => this.validateSeparateDirections(), 100);
      }
    });
    
    // 页面加载后自动打开文件夹选择对话框
    this.$nextTick(() => {
      // 稍微延迟初始化模态框，确保DOM完全加载
      setTimeout(() => {
        if (this.$refs.audioFolderModal) {
          this.$refs.audioFolderModal.show();
        } else {
          console.error('无法找到音频文件夹模态框引用');
        }
      }, 500);
      
      // 额外延迟确保所有组件完全加载后再次检测和同步
      setTimeout(() => {
        console.log('组件完全加载后再次检测上下行不同设置...');
        this.forceDetectSeparateDirections();
        this.syncSeparateDirectionsToComponent();
        
        // 再次延迟验证最终状态
        setTimeout(() => {
          this.validateSeparateDirections();
        }, 200);
      }, 1000);
    });
  },
  methods: {
    // 批量写入现有站点的多语言名称（不新增站点）
    handleBulkUpdateStationNames(payload) {
      try {
        const { direction, langCode, values, overwriteMode, startIndex } = payload || {};
        if (!langCode) {
          alert('目标语言代码为空');
          return;
        }
        const dir = direction === 'down' ? 'down' : 'up';
        const source = dir === 'up' ? this.upDirectionStations : this.downDirectionStations;
        if (!Array.isArray(source) || source.length === 0) {
          alert('当前方向暂无站点，无法写入');
          return;
        }
        const begin = Math.max(0, Number(startIndex) || 0);
        const field = 'name_' + langCode;

        let changed = 0;
        let skipped = 0;
        let ignoredOverflow = 0;

        // 计算会被使用的值范围
        const maxUsable = Math.max(0, source.length - begin);
        if (values.length > maxUsable) {
          ignoredOverflow = values.length - maxUsable;
        }

        const updated = source.map((st, i) => {
          // 不在写入范围内
          if (i < begin || i >= begin + values.length) return st;

          const v = (values[i - begin] ?? '').trim();
          // 跳过空行策略
          if (overwriteMode === 'skip-blank-lines' && v === '') {
            skipped++;
            return st;
          }

          // fill-blank 仅当原字段为空时写入
          if (overwriteMode === 'fill-blank') {
            const existing = (st[field] || '').trim();
            if (existing) {
              skipped++;
              return st;
            }
          }

          // 其他情况：写入（若v为空且策略不是skip-blank-lines，通常不会出现，因为子组件已过滤空行）
          const newSt = { ...st };
          newSt[field] = v;
          changed++;
          return newSt;
        });

        if (dir === 'up') {
          this.upDirectionStations = updated;
        } else {
          this.downDirectionStations = updated;
        }

        // 保存缓存
        this.saveCachedData();

        // 反馈结果
        let msg = `写入完成：成功 ${changed} 条，跳过 ${skipped} 条`;
        if (ignoredOverflow > 0) {
          msg += `，超出 ${ignoredOverflow} 条未匹配到站点（已忽略）`;
        }
        alert(msg);
      } catch (e) {
        console.error('批量写入多语言站名失败:', e);
        alert('批量写入失败：' + (e?.message || '未知错误'));
      }
    },
    closeSidebarOnMobile() {
      if (window && window.innerWidth <= 768) this.isSidebarOpen = false;
    },
    // 加载缓存数据
    loadCachedData() {
      try {
        // 加载站点信息
        const stationsData = cacheManager.loadStations();
        this.upDirectionStations = stationsData.upDirection;
        this.downDirectionStations = stationsData.downDirection;
        
        // 加载线路基本信息
        const lineInfo = cacheManager.loadLineInfo();
        this.lineName = lineInfo.lineName;
        this.lineType = lineInfo.lineType;
        this.currentDirection = lineInfo.currentDirection;
        
        // 加载音频播放列表
        const audioData = cacheManager.loadAudioSequences();
        this.defaultArrivalAudioSequence = audioData.defaultArrivalAudioSequence;
        this.defaultDepartureAudioSequence = audioData.defaultDepartureAudioSequence;
        this.welcomeAudioSequence = audioData.welcomeAudioSequence;
        this.terminalAudioSequence = audioData.terminalAudioSequence;
        this.upArrivalAudioSequence = audioData.upArrivalAudioSequence;
        this.downArrivalAudioSequence = audioData.downArrivalAudioSequence;
        this.upDepartureAudioSequence = audioData.upDepartureAudioSequence;
        this.downDepartureAudioSequence = audioData.downDepartureAudioSequence;
        this.upWelcomeAudioSequence = audioData.upWelcomeAudioSequence;
        this.downWelcomeAudioSequence = audioData.downWelcomeAudioSequence;
        this.upTerminalAudioSequence = audioData.upTerminalAudioSequence;
        this.downTerminalAudioSequence = audioData.downTerminalAudioSequence;
        this.serviceAudioSequences = audioData.serviceAudioSequences;
        
        // 先从缓存加载separateDirections，然后进行智能检测并可能覆盖
        const cachedSeparateDirections = audioData.separateDirections;
        this.separateDirections = { ...cachedSeparateDirections };
        
        // 立即进行智能检测，这可能会覆盖缓存中的设置
        this.forceDetectSeparateDirections();
        
        // 立即保存更新后的separateDirections到缓存
        this.saveCachedData();
        
        // 再次使用nextTick确保组件完全渲染后同步状态
        this.$nextTick(() => {
          this.syncSeparateDirectionsToComponent();
        });
        
        // 加载语言设置
        const languageData = cacheManager.loadLanguageSettings();
        this.languageSettings = languageData;
        
        // 加载模板设置
        const templateData = cacheManager.loadTemplates();
        this.arrivalTemplate = templateData.arrivalTemplate;
        this.departureTemplate = templateData.departureTemplate;
        
        // 加载项目文件夹路径
        const folderData = cacheManager.loadAudioFolders();
        this.projectFolderPath = folderData.projectFolderPath || '';
        
        console.log('缓存数据加载完成');
      } catch (error) {
        console.error('加载缓存数据失败:', error);
      }
    },
    
    // 智能检测是否应该启用上下行不同选项
    autoDetectSeparateDirections() {
      let hasChanges = false;
      
      // 检测进站语是否应该启用上下行不同
      if (this.shouldEnableSeparateDirection('arrival', this.upArrivalAudioSequence, this.downArrivalAudioSequence)) {
        if (!this.separateDirections.arrival) {
          this.separateDirections.arrival = true;
          hasChanges = true;
        }
      }
      
      // 检测出站语是否应该启用上下行不同
      if (this.shouldEnableSeparateDirection('departure', this.upDepartureAudioSequence, this.downDepartureAudioSequence)) {
        if (!this.separateDirections.departure) {
          this.separateDirections.departure = true;
          hasChanges = true;
        }
      }
      
      // 检测欢迎语是否应该启用上下行不同
      if (this.shouldEnableSeparateDirection('welcome', this.upWelcomeAudioSequence, this.downWelcomeAudioSequence)) {
        if (!this.separateDirections.welcome) {
          this.separateDirections.welcome = true;
          hasChanges = true;
        }
      }
      
      // 检测终点站语是否应该启用上下行不同
      if (this.shouldEnableSeparateDirection('terminal', this.upTerminalAudioSequence, this.downTerminalAudioSequence)) {
        if (!this.separateDirections.terminal) {
          this.separateDirections.terminal = true;
          hasChanges = true;
        }
      }
      
      if (hasChanges) {
        console.log('自动检测并更新上下行不同设置:', this.separateDirections);
        
        // 使用nextTick确保组件完全初始化后再同步数据
        this.$nextTick(() => {
          // separateDirections 通过props自动更新，但为了确保同步，也可以直接更新子组件
          if (this.$refs.audioTemplatePanel) {
            this.$refs.audioTemplatePanel.separateDirections = { ...this.separateDirections };
          }
        });
      }
    },
    
    // 强制检测是否应该启用上下行不同选项（忽略当前设置）
    forceDetectSeparateDirections() {
      let hasChanges = false;
      console.log('开始强制检测上下行不同设置...');
      
      // 检测进站语是否应该启用上下行不同
      if (this.shouldEnableSeparateDirectionForce('arrival', this.upArrivalAudioSequence, this.downArrivalAudioSequence)) {
        this.separateDirections.arrival = true;
        hasChanges = true;
        console.log('强制启用进站语上下行不同');
      }
      
      // 检测出站语是否应该启用上下行不同
      if (this.shouldEnableSeparateDirectionForce('departure', this.upDepartureAudioSequence, this.downDepartureAudioSequence)) {
        this.separateDirections.departure = true;
        hasChanges = true;
        console.log('强制启用出站语上下行不同');
      }
      
      // 检测欢迎语是否应该启用上下行不同
      if (this.shouldEnableSeparateDirectionForce('welcome', this.upWelcomeAudioSequence, this.downWelcomeAudioSequence)) {
        this.separateDirections.welcome = true;
        hasChanges = true;
        console.log('强制启用欢迎语上下行不同');
      }
      
      // 检测终点站语是否应该启用上下行不同
      if (this.shouldEnableSeparateDirectionForce('terminal', this.upTerminalAudioSequence, this.downTerminalAudioSequence)) {
        this.separateDirections.terminal = true;
        hasChanges = true;
        console.log('强制启用终点站语上下行不同');
      }
      
      if (hasChanges) {
        console.log('强制检测完成，更新了上下行不同设置:', this.separateDirections);
      } else {
        console.log('强制检测完成，无需更改设置');
      }
    },
    
    // 同步separateDirections到子组件
    syncSeparateDirectionsToComponent() {
      // 使用延迟确保子组件完全加载
      setTimeout(() => {
        if (this.$refs.audioTemplatePanel) {
          this.$refs.audioTemplatePanel.separateDirections = { ...this.separateDirections };
          console.log('已同步separateDirections到AudioTemplatePanel组件:', this.separateDirections);
        } else {
          console.warn('未找到audioTemplatePanel组件引用');
        }
      }, 100);
    },
    
    // 强制判断是否应该启用某个报站类型的上下行不同（忽略当前设置）
    shouldEnableSeparateDirectionForce(type, upSequence, downSequence) {
      // 确保序列是数组
      const upSeq = Array.isArray(upSequence) ? upSequence : [];
      const downSeq = Array.isArray(downSequence) ? downSequence : [];
      
      console.log(`检测${type}:`, { upSeq: upSeq.length, downSeq: downSeq.length });
      
      // 如果上下行序列都有内容且不相同，应该启用上下行不同
      if (upSeq.length > 0 && downSeq.length > 0) {
        // 比较两个序列是否不同
        if (JSON.stringify(upSeq) !== JSON.stringify(downSeq)) {
          console.log(`${type}的上下行序列不同，应启用上下行不同`);
          return true;
        }
      }
      
      // 如果只有一个方向有序列且另一个方向为空，也应该启用上下行不同
      if ((upSeq.length > 0 && downSeq.length === 0) || (downSeq.length > 0 && upSeq.length === 0)) {
        console.log(`${type}只有一个方向有序列，应启用上下行不同`);
        return true;
      }
      
      return false;
    },
    
    // 判断是否应该启用某个报站类型的上下行不同
    shouldEnableSeparateDirection(type, upSequence, downSequence) {
      // 确保序列是数组
      const upSeq = Array.isArray(upSequence) ? upSequence : [];
      const downSeq = Array.isArray(downSequence) ? downSequence : [];
      
      // 如果上下行序列都有内容且不相同，应该启用上下行不同
      if (upSeq.length > 0 && downSeq.length > 0) {
        // 比较两个序列是否不同
        if (JSON.stringify(upSeq) !== JSON.stringify(downSeq)) {
          console.log(`检测到${type}的上下行序列不同，应启用上下行不同`);
          return true;
        }
      }
      
      // 如果只有一个方向有序列且另一个方向为空，也应该启用上下行不同
      if ((upSeq.length > 0 && downSeq.length === 0) || (downSeq.length > 0 && upSeq.length === 0)) {
        console.log(`检测到${type}只有一个方向有序列，应启用上下行不同`);
        return true;
      }
      
      return false;
    },
    
    // 验证和调试方法：检查separateDirections状态
    validateSeparateDirections() {
      console.log('=== 验证separateDirections状态 ===');
      console.log('App.vue中的separateDirections:', this.separateDirections);
      
      if (this.$refs.audioTemplatePanel) {
        console.log('AudioTemplatePanel中的separateDirections:', this.$refs.audioTemplatePanel.separateDirections);
      } else {
        console.warn('未找到audioTemplatePanel引用');
      }
      
      // 检查各个序列的状态
      console.log('音频序列状态:', {
        upArrival: this.upArrivalAudioSequence.length,
        downArrival: this.downArrivalAudioSequence.length,
        upDeparture: this.upDepartureAudioSequence.length,
        downDeparture: this.downDepartureAudioSequence.length,
        upWelcome: this.upWelcomeAudioSequence.length,
        downWelcome: this.downWelcomeAudioSequence.length,
        upTerminal: this.upTerminalAudioSequence.length,
        downTerminal: this.downTerminalAudioSequence.length,
      });
      
      console.log('=== 验证完成 ===');
    },
    
    // 保存缓存数据
    saveCachedData() {
      try {
        // 保存站点信息
        cacheManager.saveStations(this.upDirectionStations, this.downDirectionStations);
        
        // 保存线路基本信息
        cacheManager.saveLineInfo(this.lineName, this.lineType, this.currentDirection);
        
        // 保存音频播放列表
        const audioData = {
          defaultArrivalAudioSequence: this.defaultArrivalAudioSequence,
          defaultDepartureAudioSequence: this.defaultDepartureAudioSequence,
          welcomeAudioSequence: this.welcomeAudioSequence,
          terminalAudioSequence: this.terminalAudioSequence,
          upArrivalAudioSequence: this.upArrivalAudioSequence,
          downArrivalAudioSequence: this.downArrivalAudioSequence,
          upDepartureAudioSequence: this.upDepartureAudioSequence,
          downDepartureAudioSequence: this.downDepartureAudioSequence,
          upWelcomeAudioSequence: this.upWelcomeAudioSequence,
          downWelcomeAudioSequence: this.downWelcomeAudioSequence,
          upTerminalAudioSequence: this.upTerminalAudioSequence,
          downTerminalAudioSequence: this.downTerminalAudioSequence,
          serviceAudioSequences: this.serviceAudioSequences,
          separateDirections: this.separateDirections
        };
        cacheManager.saveAudioSequences(audioData);
        
        // 保存语言设置
        cacheManager.saveLanguageSettings(this.languageSettings);
        
        // 保存模板设置
        cacheManager.saveTemplates(this.arrivalTemplate, this.departureTemplate);
        
        // 保存项目文件夹路径
        cacheManager.saveAudioFolders(this.projectFolderPath, '');
        
        console.log('缓存数据保存完成');
      } catch (error) {
        console.error('保存缓存数据失败:', error);
      }
    },
    
    // 清除所有缓存
    clearAllCache() {
      if (confirm('确定要清除所有缓存数据吗？这将删除所有保存的站点信息、音频播放列表、LED设置等所有配置。')) {
        cacheManager.clearAllCache();
        alert('所有缓存数据已清除');
        // 重新加载页面以重置所有数据
        location.reload();
      }
    },
    
    // 打开音频文件夹选择模态框
    openAudioFolderModal() {
      this.$refs.audioFolderModal.show();
    },

    showZipConfigModal() {
      this.$nextTick(() => {
        if (!this.zipConfigModal) {
          const modalElement = document.getElementById('zipConfigModal');
          if (modalElement && typeof bootstrap !== 'undefined') {
            this.zipConfigModal = new bootstrap.Modal(modalElement, {
              backdrop: 'static',
              keyboard: false
            });
          }
        }
        if (this.zipConfigModal) {
          this.zipConfigModal.show();
        }
      });
    },

    hideZipConfigModal() {
      if (this.zipConfigModal) {
        this.zipConfigModal.hide();
      }
      this.selectedZipConfigIndex = 0;
      this.pendingZipConfigs = [];
    },

    async applyZipConfig() {
      if (!Array.isArray(this.pendingZipConfigs) || this.pendingZipConfigs.length === 0) {
        this.hideZipConfigModal();
        return;
      }
      if (this.selectedZipConfigIndex < 0 || this.selectedZipConfigIndex >= this.pendingZipConfigs.length) {
        alert('请选择要应用的配置文件');
        return;
      }
      if (this.isApplyingZipConfig) return;
      this.isApplyingZipConfig = true;
      try {
        const selected = this.pendingZipConfigs[this.selectedZipConfigIndex];
        if (!selected || !selected.entryPath || !this.zipEntries || !this.zipEntries[selected.entryPath]) {
          alert('无法找到所选的配置文件');
          return;
        }
        let jsonData = null;
        if (selected.rawText) {
          jsonData = JSON.parse(selected.rawText);
        } else {
          const entry = this.zipEntries[selected.entryPath];
          const text = await entry.text();
          jsonData = JSON.parse(text);
        }
        await this.resetBeforeImport();
        this.importJsonData(jsonData);
        this.saveCachedData();
        alert(`已应用压缩包中的配置：${selected.name}`);
      } catch (error) {
        console.error('应用ZIP配置失败:', error);
        alert('应用压缩包中的配置失败：' + (error && error.message ? error.message : '未知错误'));
      } finally {
        this.isApplyingZipConfig = false;
        this.hideZipConfigModal();
      }
    },
    
    // 处理文件夹选择
    async handleFolderSelected(folderData) {
      this.projectFolder = folderData.projectFolder;
      this.sourceType = folderData.sourceType || (this.projectFolder ? 'directory' : null);
      this.zipEntries = folderData.zipEntries || null;
      this.zipAudioPaths = folderData.zipAudioPaths || [];
      this.zipFileName = folderData.zipFileName || '';
      this.pendingZipConfigs = Array.isArray(folderData.zipConfigFiles) ? folderData.zipConfigFiles : [];
      this.selectedZipConfigIndex = 0;

      // 基础的子文件夹列表
  // 注意：不能使用 JSON 深拷贝，否则会丢失 FileSystemDirectoryHandle（非可序列化对象）
  // 我们显式拷贝需要的字段并保留 handle 引用
  this.audioFolders = Array.isArray(folderData.subFolders)
    ? folderData.subFolders.map(sf => ({
        name: sf.name,
        handle: sf.handle || null,
        audioCount: sf.audioCount || 0,
      }))
    : [];

      // 若存在根目录音频，追加一个“根目录”伪文件夹以便在UI中展示
      if (folderData.rootAudioCount && folderData.rootAudioCount > 0) {
        // 目录模式下使用 handle 指向根，用于仅读取根层文件
        if (this.sourceType === 'directory' && this.projectFolder) {
          this.audioFolders.push({ name: '根目录', handle: this.projectFolder, audioCount: folderData.rootAudioCount, isRoot: true });
        } else if (this.sourceType === 'zip') {
          // ZIP 模式无需 handle
          this.audioFolders.push({ name: '根目录', handle: null, audioCount: folderData.rootAudioCount, isRoot: true });
        }
      }
      
      // 加载音频文件列表
      await this.loadAudioFiles();
      
      // 保存缓存
      this.saveCachedData();

      if (this.sourceType === 'zip' && this.pendingZipConfigs.length > 0) {
        this.showZipConfigModal();
      }
    },
    
    // 加载音频文件
    async loadAudioFiles() {
      try {
        const exts = ['mp3', 'wav', 'ogg', 'm4a', 'flac', 'aac', 'wma'];

        // 重置音频文件夹数据
        this.audioFolders.forEach(folder => {
          folder.audios = [];
        });

        if (this.sourceType === 'directory' && this.projectFolder) {
          // 保存项目文件夹名称
          this.projectFolderPath = this.projectFolder.name;

          for (const folder of this.audioFolders) {
            try {
              if (folder.isRoot) {
                // 根目录：仅读取根层文件，不递归
                const audioFiles = await this.getFilesInSingleDirectory(folder.handle, exts);
                folder.audios = audioFiles.map(file => ({
                  name: file.name,
                  file: file,
                  url: URL.createObjectURL(file),
                  // 在路径中加上“根目录/”标识，便于导出的JSON中保留
                  path: `根目录/${file.name}`,
                  folderName: '根目录'
                }));
              } else if (folder.handle) {
                // 子文件夹：递归读取
                const audioFiles = await this.getFilesFromFolder(folder.handle, exts);
                folder.audios = audioFiles.map(file => ({
                  name: file.name,
                  file: file,
                  url: URL.createObjectURL(file),
                  path: `${folder.name}/${file.name}`,
                  folderName: folder.name
                }));
              }
            } catch (error) {
              console.error(`加载文件夹 ${folder.name} 中的音频文件失败:`, error);
            }
          }
        } else if (this.sourceType === 'zip' && this.zipEntries) {
          // ZIP 模式：根据 zipAudioPaths 构建各文件夹 audios
          // 创建一个索引：顶层目录名（''表示根） -> 对应 audioFolders 项
          const folderIndex = new Map();
          for (const folder of this.audioFolders) {
            const key = folder.name === '根目录' ? '' : folder.name;
            folderIndex.set(key, folder);
          }

          // 逐个读取 Blob 并填充 audios
          for (const audioPathObj of this.zipAudioPaths) {
            // 兼容旧版本（字符串格式）和新版本（对象格式）
            const originalPath = typeof audioPathObj === 'string' ? audioPathObj : audioPathObj.original;
            const normalizedPath = typeof audioPathObj === 'string' ? audioPathObj : audioPathObj.normalized;
            const targetFolderName = typeof audioPathObj === 'string' ? null : audioPathObj.targetFolder;
            
            const lower = normalizedPath.toLowerCase();
            if (!exts.some(ext => lower.endsWith('.' + ext))) continue;
            const entry = this.zipEntries[originalPath];
            // JSZip 使用 dir 属性判断是否为目录
            if (!entry || entry.dir) continue;

            // 使用预先计算的 targetFolder，如果没有则回退到旧逻辑
            let folderKey = '';
            if (targetFolderName !== null && targetFolderName !== undefined) {
              folderKey = targetFolderName;
            } else {
              // 向后兼容：使用旧的路径解析逻辑
              const parts = normalizedPath.split('/').filter(Boolean);
              folderKey = parts.length > 1 ? parts[0] : '';
            }
            
            let folder = folderIndex.get(folderKey);
            if (!folder) {
              // 如果未在子文件夹列表中，创建一个新分组（健壮性）
              const newFolder = { 
                name: folderKey || '根目录', 
                handle: null, 
                audioCount: 0, 
                isRoot: folderKey === '', 
                audios: [] 
              };
              this.audioFolders.push(newFolder);
              folderIndex.set(folderKey, newFolder);
              folder = newFolder;
            }
            
            try {
              // JSZip 使用 async('blob') 读取 Blob
              const blob = await entry.async('blob');
              const url = URL.createObjectURL(blob);
              const parts = normalizedPath.split('/').filter(Boolean);
              const fileName = parts[parts.length - 1];
              
              if (!folder.audios) folder.audios = [];
              folder.audios.push({
                name: fileName,
                file: blob,
                url,
                // ZIP 模式下，使用规范化后的路径，根目录音频加上"根目录/"标识
                path: (folderKey === '' ? `根目录/${fileName}` : normalizedPath),
                folderName: folder.name
              });
            } catch (e) {
              console.error('读取ZIP条目失败:', originalPath, e);
            }
          }
        }
      } catch (error) {
        console.error('加载音频文件失败:', error);
        alert('加载音频文件失败: ' + error.message);
      }
    },
    
    // 从文件夹获取文件列表
    async getFilesFromFolder(folderHandle, fileExtensions) {
      const files = [];
      
      // 递归遍历文件夹
      async function getFilesRecursively(handle) {
        if (handle.kind === 'file') {
          // 检查文件扩展名
          const fileName = handle.name.toLowerCase();
          if (fileExtensions.some(ext => fileName.endsWith('.' + ext))) {
            const file = await handle.getFile();
            files.push(file);
          }
        } else if (handle.kind === 'directory') {
          for await (const entry of handle.values()) {
            await getFilesRecursively(entry);
          }
        }
      }
      
      await getFilesRecursively(folderHandle);
      return files;
    },
    
    // 仅获取指定目录（不递归）的音频文件（用于根目录）
    async getFilesInSingleDirectory(dirHandle, fileExtensions) {
      const files = [];
      for await (const entry of dirHandle.values()) {
        if (entry.kind === 'file') {
          const fileName = entry.name.toLowerCase();
          if (fileExtensions.some(ext => fileName.endsWith('.' + ext))) {
            const file = await entry.getFile();
            files.push(file);
          }
        }
      }
      return files;
    },
    
    // 导入站点数据
    importStations(stations, direction) {
      if (direction === 'up') {
        // 追加模式：在现有上行站点列表末尾添加
        this.upDirectionStations = [...this.upDirectionStations, ...stations];
      } else {
        // 追加模式：在现有下行站点列表末尾添加
        this.downDirectionStations = [...this.downDirectionStations, ...stations];
      }
      
      // 保存缓存
      this.saveCachedData();
      
      // 不再在这里显示成功消息，而是由子组件通过事件通知我们
    },
    
    // 处理站点导入成功事件
    handleImportSuccess(stationCount) {
      // 显示成功消息
      alert(`成功导入 ${stationCount} 个站点`);
    },
    
    // 处理下行站点生成成功事件
    handleReverseSuccess(stationCount) {
      // 显示成功消息
      alert(`已成功生成下行站点，共${stationCount}个站点`);
    },
    
    // 添加单个站点
    addStation(station, direction) {
      if (direction === 'up') {
        this.upDirectionStations.push(station);
      } else {
        this.downDirectionStations.push(station);
      }
      
      // 保存缓存
      this.saveCachedData();
      
      // 自动选择新添加的站点
      const newIndex = this.currentStations.length - 1;
      this.selectStation(newIndex);
    },
    
    // 删除单个站点
    removeStation(index, direction) {
      if (direction === 'up') {
        this.upDirectionStations.splice(index, 1);
      } else {
        this.downDirectionStations.splice(index, 1);
      }
      
      // 保存缓存
      this.saveCachedData();
      
      // 重置选中的站点
      if (this.selectedStationIndex >= this.currentStations.length) {
        this.selectedStationIndex = this.currentStations.length - 1;
      } else if (this.currentStations.length === 0) {
        this.selectedStationIndex = -1;
      }
    },
    
    // 清空所有站点
    clearStations(direction) {
      if (direction === 'up') {
        this.upDirectionStations = [];
      } else {
        this.downDirectionStations = [];
      }
      
      // 保存缓存
      this.saveCachedData();
      
      // 重置选中的站点
      this.selectedStationIndex = -1;
    },
    
    // 移动站点位置
    moveStation(fromIndex, toIndex, direction) {
      const stations = direction === 'up' ? this.upDirectionStations : this.downDirectionStations;
      
      // 交换站点位置
      const temp = stations[fromIndex];
      stations.splice(fromIndex, 1);
      stations.splice(toIndex, 0, temp);
      
      // 保存缓存
      this.saveCachedData();
      
      // 更新选中的站点
      this.selectedStationIndex = toIndex;
    },
    
    // 切换方向（上行/下行）
    switchDirection(direction) {
      if (this.currentDirection === direction) return;
      this.currentDirection = direction;
      this.selectedStationIndex = -1; // 重置选中的站点
      
      // 保存缓存
      this.saveCachedData();
    },
    
    // 选择站点
    selectStation(index) {
      this.selectedStationIndex = index;
    },
    
    // 打开站点编辑模态框
    openStationEditModal(index) {
      if (index >= 0 && index < this.currentStations.length) {
        // 克隆站点对象以避免直接修改
        this.editingStation = JSON.parse(JSON.stringify(this.currentStations[index]));
        this.editingStation.index = index;
        this.$refs.stationEditModal.show();
      }
    },
    
    // 保存站点编辑
    saveStationEdit(station) {
      const index = station.index;
      delete station.index; // 移除辅助字段
      
      if (this.currentDirection === 'up') {
        this.upDirectionStations[index] = station;
      } else {
        this.downDirectionStations[index] = station;
      }
      
      // 保存缓存
      this.saveCachedData();
    },
    
    // 筛选语言设置，只返回count指定数量的语言
    filterLanguageSettings() {
      const enabled = this.languageSettings.enabled || false;
      
      if (!enabled) {
        // 当多语言功能关闭时，只返回中文语言
        return {
          enabled: false,
          count: 1,
          languages: [{ name: '中文', code: 'zh' }]
        };
      }
      
      const count = this.languageSettings.count || 1;
      const filteredLanguages = this.languageSettings.languages.slice(0, count);
      
      return {
        enabled: enabled,
        count: count,
        languages: filteredLanguages
      };
    },

    // 更新线路名称
    updateLineName(name) {
      this.lineName = name;
      // 保存缓存
      this.saveCachedData();
    },
    
    // 更新线路类型
    updateLineType(newLineType) {
      this.lineType = newLineType;
      // 保存缓存
      this.saveCachedData();
    },
    
    // 生成JSON数据
    generateJsonData(audioConfig) {
      const currentLineType = this.lineType;
      const filteredLanguageSettings = this.filterLanguageSettings();
      
      // 处理LED设置，只保留有内容的字段
      const ledSettingsWithContent = {
        stationTextColor: this.ledSettings.stationTextColor || '255',
        serviceTextColor: this.ledSettings.serviceTextColor || '65280',
        stationScrollMode: 'left', // 强制设置为left，不可通过界面修改
        serviceScrollMode: 'left', // 强制设置为left，不可通过界面修改
        stationNoScrollMode: this.ledSettings.stationNoScrollMode || 'left',
        serviceNoScrollMode: this.ledSettings.serviceNoScrollMode || 'left',
        leftScrollSpeed: this.ledSettings.leftScrollSpeed || 10,
        upScrollSpeed: this.ledSettings.upScrollSpeed || 5
      };
      
      // 只添加有内容的LED显示内容
      const ledContentFields = [
        'arrivalLEDContent', 'departureLEDContent', 'welcomeLEDContent',
        'service1LEDContent', 'service2LEDContent', 'service3LEDContent', 'service4LEDContent', 'service5LEDContent',
        'service6LEDContent', 'service7LEDContent', 'service8LEDContent', 'service9LEDContent', 'service10LEDContent'
      ];
      
      ledContentFields.forEach(field => {
        if (this.ledSettings[field] && this.ledSettings[field].trim() !== '') {
          ledSettingsWithContent[field] = this.ledSettings[field];
        }
      });
      
      // 处理多语言LED内容，只添加有内容的字段
      if (filteredLanguageSettings.enabled && filteredLanguageSettings.languages) {
        filteredLanguageSettings.languages.forEach(lang => {
          if (lang && lang.code && lang.code !== 'zh') {
            ledContentFields.forEach(field => {
              const multiLangField = field + '_' + lang.code;
              if (this.ledSettings[multiLangField] && this.ledSettings[multiLangField].trim() !== '') {
                ledSettingsWithContent[multiLangField] = this.ledSettings[multiLangField];
              }
            });
          }
        });
      }
      
      // 构建站点数据
      const stationsData = {
        upDirectionStations: this.upDirectionStations.map(station => {
          const stationData = {
            ...station,
            audioSequence: station.audioSequence || [],
            arrivalAudioSequence: station.arrivalAudioSequence || [],
            departureAudioSequence: station.departureAudioSequence || [],
            multiLanguageSequence: station.multiLanguageSequence || [],
            aliasSequence: station.aliasSequence || [],
            latitude: station.latitude || 0,
            longitude: station.longitude || 0,
            arrivalDistance: station.arrivalDistance || 0,
            departureDistance: station.departureDistance || 0
          };
          
          // 只添加有内容的LED字段
          if (station.arrivalLEDContent && station.arrivalLEDContent.trim() !== '') {
            stationData.arrivalLEDContent = station.arrivalLEDContent;
          }
          if (station.departureLEDContent && station.departureLEDContent.trim() !== '') {
            stationData.departureLEDContent = station.departureLEDContent;
          }
          
          // 添加多语言LED内容（只添加有内容的字段）
          if (filteredLanguageSettings.enabled && filteredLanguageSettings.languages) {
            filteredLanguageSettings.languages.forEach(lang => {
              if (lang && lang.code && lang.code !== 'zh') {
                const arrivalField = 'arrivalLEDContent_' + lang.code;
                const departureField = 'departureLEDContent_' + lang.code;
                
                if (station[arrivalField] && station[arrivalField].trim() !== '') {
                  stationData[arrivalField] = station[arrivalField];
                }
                if (station[departureField] && station[departureField].trim() !== '') {
                  stationData[departureField] = station[departureField];
                }
              }
            });
          }
          
          return stationData;
        }),
        // 始终包含下行方向站点
        downDirectionStations: this.downDirectionStations.map(station => {
          const stationData = {
            ...station,
            audioSequence: station.audioSequence || [],
            arrivalAudioSequence: station.arrivalAudioSequence || [],
            departureAudioSequence: station.departureAudioSequence || [],
            multiLanguageSequence: station.multiLanguageSequence || [],
            aliasSequence: station.aliasSequence || [],
            latitude: station.latitude || 0,
            longitude: station.longitude || 0,
            arrivalDistance: station.arrivalDistance || 0,
            departureDistance: station.departureDistance || 0
          };
          
          // 只添加有内容的LED字段
          if (station.arrivalLEDContent && station.arrivalLEDContent.trim() !== '') {
            stationData.arrivalLEDContent = station.arrivalLEDContent;
          }
          if (station.departureLEDContent && station.departureLEDContent.trim() !== '') {
            stationData.departureLEDContent = station.departureLEDContent;
          }
          
          // 添加多语言LED内容（只添加有内容的字段）
          if (filteredLanguageSettings.enabled && filteredLanguageSettings.languages) {
            filteredLanguageSettings.languages.forEach(lang => {
              if (lang && lang.code && lang.code !== 'zh') {
                const arrivalField = 'arrivalLEDContent_' + lang.code;
                const departureField = 'departureLEDContent_' + lang.code;
                
                if (station[arrivalField] && station[arrivalField].trim() !== '') {
                  stationData[arrivalField] = station[arrivalField];
                }
                if (station[departureField] && station[departureField].trim() !== '') {
                  stationData[departureField] = station[departureField];
                }
              }
            });
          }
          
          return stationData;
        })
      };

      // 如果是单向线路，确保downDirectionStations为空数组
      if (currentLineType === 'oneWay') {
        stationsData.downDirectionStations = [];
      }

      // 如果提供了音频配置，保存相关序列
      if (audioConfig) {
        this.defaultArrivalAudioSequence = audioConfig.arrivalAudioSequence || [];
        this.defaultDepartureAudioSequence = audioConfig.departureAudioSequence || [];
        this.welcomeAudioSequence = audioConfig.welcomeAudioSequence || [];
        this.terminalAudioSequence = audioConfig.terminalAudioSequence || [];
        // 保存上下行分别的配置
        this.separateDirections = audioConfig.separateDirections || {
          arrival: false,
          departure: false,
          welcome: false,
          terminal: false
        };
        this.upArrivalAudioSequence = audioConfig.upArrivalAudioSequence || [];
        this.downArrivalAudioSequence = audioConfig.downArrivalAudioSequence || [];
        this.upDepartureAudioSequence = audioConfig.upDepartureAudioSequence || [];
        this.downDepartureAudioSequence = audioConfig.downDepartureAudioSequence || [];
        this.upWelcomeAudioSequence = audioConfig.upWelcomeAudioSequence || [];
        this.downWelcomeAudioSequence = audioConfig.downWelcomeAudioSequence || [];
        this.upTerminalAudioSequence = audioConfig.upTerminalAudioSequence || [];
        this.downTerminalAudioSequence = audioConfig.downTerminalAudioSequence || [];
        this.serviceAudioSequences = audioConfig.serviceAudioSequences || Array(10).fill().map(() => []);
        if (audioConfig.languageSettings) {
          this.languageSettings = audioConfig.languageSettings;
        }
        
        // 保存缓存
        this.saveCachedData();
      }

      // 构建JSON数据对象
      const jsonData = {
        version: '1.0',
        lineName: this.lineName, // 使用App组件中的lineName
        lineType: currentLineType,
        languageSettings: filteredLanguageSettings,
        ...stationsData,
        defaultConfig: {
          arrivalAudioSequence: this.defaultArrivalAudioSequence || [],
          departureAudioSequence: this.defaultDepartureAudioSequence || [],
          welcomeAudioSequence: this.welcomeAudioSequence || [],
          terminalAudioSequence: this.terminalAudioSequence || [],
          // 上下行分别的配置
          separateDirections: this.separateDirections,
          upArrivalAudioSequence: this.upArrivalAudioSequence || [],
          downArrivalAudioSequence: this.downArrivalAudioSequence || [],
          upDepartureAudioSequence: this.upDepartureAudioSequence || [],
          downDepartureAudioSequence: this.downDepartureAudioSequence || [],
          upWelcomeAudioSequence: this.upWelcomeAudioSequence || [],
          downWelcomeAudioSequence: this.downWelcomeAudioSequence || [],
          upTerminalAudioSequence: this.upTerminalAudioSequence || [],
          downTerminalAudioSequence: this.downTerminalAudioSequence || [],
          serviceAudioSequences: this.serviceAudioSequences,
          arrivalDistance: 50,
          departureDistance: 50,
          ledSettings: ledSettingsWithContent
        }
      };

      // 单向线路导出时，清空所有下行的模板序列，保证JSON只保留当前状态
      if (currentLineType === 'oneWay') {
        jsonData.defaultConfig.downArrivalAudioSequence = [];
        jsonData.defaultConfig.downDepartureAudioSequence = [];
        jsonData.defaultConfig.downWelcomeAudioSequence = [];
        jsonData.defaultConfig.downTerminalAudioSequence = [];
      }

      return jsonData;
    },
    
    // 获取实际使用的音频文件
    getUsedAudioFiles() {
      // 从上下行站点的音频序列中收集所有使用的音频文件
      const usedPaths = new Set();
      
      // 从默认序列中收集
      if (this.defaultArrivalAudioSequence) {
        this.defaultArrivalAudioSequence.forEach(item => {
          if (typeof item === 'string' && !item.startsWith('【') && 
             // 检查是否是文件路径（包含斜杠）或直接文件名
             (item.includes('/') || item.includes('.'))) {
            usedPaths.add(item);
          }
        });
      }
      
      if (this.defaultDepartureAudioSequence) {
        this.defaultDepartureAudioSequence.forEach(item => {
          if (typeof item === 'string' && !item.startsWith('【') && 
             (item.includes('/') || item.includes('.'))) {
            usedPaths.add(item);
          }
        });
      }
      
      if (this.welcomeAudioSequence) {
        this.welcomeAudioSequence.forEach(item => {
          if (typeof item === 'string' && !item.startsWith('【') && 
             (item.includes('/') || item.includes('.'))) {
            usedPaths.add(item);
          }
        });
      }
      
      if (this.terminalAudioSequence) {
        this.terminalAudioSequence.forEach(item => {
          if (typeof item === 'string' && !item.startsWith('【') && 
             (item.includes('/') || item.includes('.'))) {
            usedPaths.add(item);
          }
        });
      }
      
      // 从服务语音频序列中收集
      if (this.serviceAudioSequences) {
        this.serviceAudioSequences.forEach(sequence => {
          if (sequence && Array.isArray(sequence)) {
            sequence.forEach(item => {
              if (typeof item === 'string' && !item.startsWith('【') && 
                 (item.includes('/') || item.includes('.'))) {
                usedPaths.add(item);
              }
            });
          }
        });
      }
      
      // 从站点自定义序列中收集
      const allStations = [...this.upDirectionStations, ...this.downDirectionStations];
      allStations.forEach(station => {
        if (station.audioSequence) {
          station.audioSequence.forEach(item => {
            if (typeof item === 'string' && !item.startsWith('【') && 
               (item.includes('/') || item.includes('.'))) {
              usedPaths.add(item);
            }
          });
        }
        
        if (station.arrivalAudioSequence) {
          station.arrivalAudioSequence.forEach(item => {
            if (typeof item === 'string' && !item.startsWith('【') && 
               (item.includes('/') || item.includes('.'))) {
              usedPaths.add(item);
            }
          });
        }
        
        if (station.departureAudioSequence) {
          station.departureAudioSequence.forEach(item => {
            if (typeof item === 'string' && !item.startsWith('【') && 
               (item.includes('/') || item.includes('.'))) {
              usedPaths.add(item);
            }
          });
        }
        
        if (station.multiLanguageSequence) {
          station.multiLanguageSequence.forEach(item => {
            if (typeof item === 'string' && !item.startsWith('【') && 
               (item.includes('/') || item.includes('.'))) {
              usedPaths.add(item);
            }
          });
        }
        
        if (station.aliasSequence) {
          station.aliasSequence.forEach(item => {
            if (typeof item === 'string' && !item.startsWith('【') && 
               (item.includes('/') || item.includes('.'))) {
              usedPaths.add(item);
            }
          });
        }
      });
      
      // 将路径转换为文件名信息
      const usedAudios = Array.from(usedPaths).map(path => {
        // 从所有音频文件夹中查找对应的音频
        let audio = null;
        let folderName = '';
        
        for (const folder of this.audioFolders) {
          if (folder.audios) {
            audio = folder.audios.find(a => a.path === path);
            if (audio) {
              folderName = folder.name;
              break;
            }
          }
        }
        
        return {
          name: audio ? audio.name : path.split('/').pop(),
          path: path,
          // 添加文件夹信息，方便恢复时定位
          folder: folderName
        };
      });
      
      return usedAudios;
    },
    
    // 下载JSON文件
    downloadJsonFile() {
      console.log('📥 准备下载JSON配置文件...');
      
      // 方案2：导出前强制从子组件同步最新数据
      if (this.$refs.audioTemplatePanel) {
        console.log('🔄 从音频模板面板强制同步最新数据...');
        this.$refs.audioTemplatePanel.forceSyncAudioConfig();
      }
      
      // 使用nextTick确保事件已处理
      this.$nextTick(() => {
        // 再次确保缓存已保存
        this.saveCachedData();
        
        console.log('📊 当前音频序列状态:', {
          welcomeAudioSequence: this.welcomeAudioSequence,
          terminalAudioSequence: this.terminalAudioSequence,
          upWelcomeAudioSequence: this.upWelcomeAudioSequence,
          downWelcomeAudioSequence: this.downWelcomeAudioSequence,
          upTerminalAudioSequence: this.upTerminalAudioSequence,
          downTerminalAudioSequence: this.downTerminalAudioSequence
        });
        
        // 生成最新的JSON数据
        const jsonData = this.generateJsonData();
        const jsonString = JSON.stringify(jsonData, null, 2);
        
        // 创建Blob对象
        const blob = new Blob([jsonString], { type: 'application/json' });
        
        // 创建下载链接
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'bus_station_config.json';
        document.body.appendChild(a);
        a.click();
        
        // 清理
        setTimeout(() => {
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }, 0);
        
        console.log('✅ JSON文件下载成功');
      });
    },

    // 下载压缩包
    async downloadZipPackage() {
      try {
        console.log('📦 准备下载线路压缩包...');
        
        // 检查是否有线路名称
        if (!this.lineName) {
          alert('请先设置线路名称');
          return;
        }

        // 检查是否有音频文件
        if (!this.audioFolders.length || this.audioFolders.every(folder => !folder.audios || !folder.audios.length)) {
          alert('请先选择项目文件夹');
          return;
        }

        // 方案2：导出前强制从子组件同步最新数据
        if (this.$refs.audioTemplatePanel) {
          console.log('🔄 从音频模板面板强制同步最新数据...');
          this.$refs.audioTemplatePanel.forceSyncAudioConfig();
          
          // 等待nextTick确保事件已处理
          await this.$nextTick();
          
          // 再次确保缓存已保存
          this.saveCachedData();
          
          console.log('📊 当前音频序列状态:', {
            welcomeAudioSequence: this.welcomeAudioSequence,
            terminalAudioSequence: this.terminalAudioSequence,
            upWelcomeAudioSequence: this.upWelcomeAudioSequence,
            downWelcomeAudioSequence: this.downWelcomeAudioSequence,
            upTerminalAudioSequence: this.upTerminalAudioSequence,
            downTerminalAudioSequence: this.downTerminalAudioSequence
          });
        }

        const jsonData = this.generateJsonData();
        const zip = new JSZip();
        
        // 添加JSON配置
        const jsonBlob = new Blob([JSON.stringify(jsonData, null, 2)], { type: 'application/json' });
        zip.file(`${this.lineName}_配置.json`, jsonBlob);
        
        // 为每个子文件夹创建对应的文件夹结构
        for (const folder of this.audioFolders) {
          if (folder.audios && folder.audios.length > 0) {
            const subFolder = zip.folder(folder.name);
            
            // 添加该文件夹下的所有音频文件
            for (const audio of folder.audios) {
              try {
                if (audio.file) {
                  subFolder.file(audio.name, audio.file);
                } else if (audio.url) {
                  const response = await fetch(audio.url);
                  if (response.ok) {
                    const blob = await response.blob();
                    subFolder.file(audio.name, blob);
                  }
                }
              } catch (error) {
                console.error(`处理音频文件失败: ${audio.name}`, error);
              }
            }
          }
        }
        
        // 生成并下载zip文件
        const zipBlob = await zip.generateAsync({ type: "blob" });
        saveAs(zipBlob, `${this.lineName}.zip`);
        console.log('✅ 线路压缩包下载成功');
      } catch (error) {
        console.error('❌ 生成压缩包失败:', error);
        alert('生成压缩包失败: ' + error.message);
      }
    },
    
    // 获取音频文件
    async getAudioFile(audio) {
      try {
        // 从所有文件夹中查找音频文件
        let audioData = null;
        for (const folder of this.audioFolders) {
          if (folder.audios) {
            audioData = folder.audios.find(a => a.path === audio.path || a.name === audio.name);
            if (audioData) break;
          }
        }
        
        if (audioData && audioData.file) {
          return audioData.file;
        } else if (audioData && audioData.url) {
          const response = await fetch(audioData.url);
          if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
          return await response.blob();
        }
        
        throw new Error(`找不到音频文件: ${audio.name}`);
      } catch (error) {
        console.error(`获取音频文件失败: ${audio.name}`, error);
        return null;
      }
    },
    
    // 在导入新配置前重置缓存与内存状态
    async resetBeforeImport() {
      try {
        cacheManager.clearAllCache();
      } catch (e) {}
      this.lineName = '';
      this.lineType = 'twoWay';
      this.upDirectionStations = [];
      this.downDirectionStations = [];
      this.defaultArrivalAudioSequence = [];
      this.defaultDepartureAudioSequence = [];
      this.welcomeAudioSequence = [];
      this.terminalAudioSequence = [];
      this.upArrivalAudioSequence = [];
      this.downArrivalAudioSequence = [];
      this.upDepartureAudioSequence = [];
      this.downDepartureAudioSequence = [];
      this.upWelcomeAudioSequence = [];
      this.downWelcomeAudioSequence = [];
      this.upTerminalAudioSequence = [];
      this.downTerminalAudioSequence = [];
      this.serviceAudioSequences = Array(10).fill().map(() => []);
      this.separateDirections = { arrival: false, departure: false, welcome: false, terminal: false };
      this.ledSettings = {
        stationTextColor: '255',
        serviceTextColor: '65280',
        stationScrollMode: 'left',
        serviceScrollMode: 'left',
        stationNoScrollMode: 'left',
        serviceNoScrollMode: 'left',
        leftScrollSpeed: 10,
        upScrollSpeed: 5,
        arrivalLEDContent: '【当前站】 到了',
        departureLEDContent: '下一站 【当前站】',
        welcomeLEDContent: '',
        service1LEDContent: '',
        service2LEDContent: '',
        service3LEDContent: '',
        service4LEDContent: '',
        service5LEDContent: '',
        service6LEDContent: '',
        service7LEDContent: '',
        service8LEDContent: '',
        service9LEDContent: '',
        service10LEDContent: '',
        arrivalLEDContent_en: '',
        departureLEDContent_en: '',
        welcomeLEDContent_en: '',
        service1LEDContent_en: '',
        service2LEDContent_en: '',
        service3LEDContent_en: '',
        service4LEDContent_en: '',
        service5LEDContent_en: '',
        service6LEDContent_en: '',
        service7LEDContent_en: '',
        service8LEDContent_en: '',
        service9LEDContent_en: '',
        service10LEDContent_en: '',
        arrivalLEDContent_ja: '',
        departureLEDContent_ja: '',
        welcomeLEDContent_ja: '',
        service1LEDContent_ja: '',
        service2LEDContent_ja: '',
        service3LEDContent_ja: '',
        service4LEDContent_ja: '',
        service5LEDContent_ja: '',
        service6LEDContent_ja: '',
        service7LEDContent_ja: '',
        service8LEDContent_ja: '',
        service9LEDContent_ja: '',
        service10LEDContent_ja: '',
        arrivalLEDContent_ko: '',
        departureLEDContent_ko: '',
        welcomeLEDContent_ko: '',
        service1LEDContent_ko: '',
        service2LEDContent_ko: '',
        service3LEDContent_ko: '',
        service4LEDContent_ko: '',
        service5LEDContent_ko: '',
        service6LEDContent_ko: '',
        service7LEDContent_ko: '',
        service8LEDContent_ko: '',
        service9LEDContent_ko: '',
        service10LEDContent_ko: '',
        arrivalLEDContent_ru: '',
        departureLEDContent_ru: '',
        welcomeLEDContent_ru: '',
        service1LEDContent_ru: '',
        service2LEDContent_ru: '',
        service3LEDContent_ru: '',
        service4LEDContent_ru: '',
        service5LEDContent_ru: '',
        service6LEDContent_ru: '',
        service7LEDContent_ru: '',
        service8LEDContent_ru: '',
        service9LEDContent_ru: '',
        service10LEDContent_ru: ''
      };
    },

    // 导入JSON数据
    importJsonData(jsonData) {
      try {
        console.log('导入JSON数据:', jsonData);
        
        // 导入线路名称
        if (jsonData.lineName) {
          this.lineName = jsonData.lineName;
        }
        
        // 处理线路类型
        if (jsonData.lineType) {
          this.lineType = jsonData.lineType;
        }
        
        // 导入站点数据
        if (jsonData.upDirectionStations) {
          this.upDirectionStations = jsonData.upDirectionStations;
        }
        
        if (jsonData.downDirectionStations) {
          this.downDirectionStations = jsonData.downDirectionStations;
        }
        
        // 导入LED显示内容模板和设置
        if (jsonData.defaultConfig) {
          // 导入LED设置
          if (jsonData.defaultConfig.ledSettings) {
            // 合并LED设置，保持现有的空白字段结构，只更新有值的字段
            const importedLedSettings = jsonData.defaultConfig.ledSettings;
            
            // 更新基础设置
            this.ledSettings.stationTextColor = importedLedSettings.stationTextColor || this.ledSettings.stationTextColor;
            this.ledSettings.serviceTextColor = importedLedSettings.serviceTextColor || this.ledSettings.serviceTextColor;
            this.ledSettings.stationNoScrollMode = importedLedSettings.stationNoScrollMode || this.ledSettings.stationNoScrollMode;
            this.ledSettings.serviceNoScrollMode = importedLedSettings.serviceNoScrollMode || this.ledSettings.serviceNoScrollMode;
            // 新字段：向左/向上滚动速度
            // 优先读取新字段；如不存在则兼容旧字段映射
            this.ledSettings.leftScrollSpeed = (importedLedSettings.leftScrollSpeed != null)
              ? importedLedSettings.leftScrollSpeed
              : (importedLedSettings.stationScrollSpeed || this.ledSettings.leftScrollSpeed);
            this.ledSettings.upScrollSpeed = (importedLedSettings.upScrollSpeed != null)
              ? importedLedSettings.upScrollSpeed
              : (importedLedSettings.serviceScrollSpeed || this.ledSettings.upScrollSpeed);
            // 强制设置滚动模式为left，不可通过导入修改
            this.ledSettings.stationScrollMode = 'left';
            this.ledSettings.serviceScrollMode = 'left';
            
            // 更新LED内容字段 - 只更新导入数据中存在的字段
            const ledContentFields = [
              'arrivalLEDContent', 'departureLEDContent', 'welcomeLEDContent',
              'service1LEDContent', 'service2LEDContent', 'service3LEDContent', 'service4LEDContent', 'service5LEDContent',
              'service6LEDContent', 'service7LEDContent', 'service8LEDContent', 'service9LEDContent', 'service10LEDContent'
            ];
            
            // 导入所有存在的字段（包括多语言字段）
            Object.keys(importedLedSettings).forEach(key => {
              if (key.startsWith('service') || key.startsWith('arrival') || key.startsWith('departure') || key.startsWith('welcome')) {
                this.ledSettings[key] = importedLedSettings[key];
              }
            });
          } else {
            // 兼容旧版本
            // 如果LED设置在defaultConfig中但不在ledSettings中
            if (jsonData.defaultConfig.arrivalLEDContent) {
              this.ledSettings.arrivalLEDContent = jsonData.defaultConfig.arrivalLEDContent;
            }
            
            if (jsonData.defaultConfig.departureLEDContent) {
              this.ledSettings.departureLEDContent = jsonData.defaultConfig.departureLEDContent;
            }
          }
          
          // 导入默认音频序列
          if (jsonData.defaultConfig.arrivalAudioSequence) {
            this.defaultArrivalAudioSequence = jsonData.defaultConfig.arrivalAudioSequence;
          }
          
          if (jsonData.defaultConfig.departureAudioSequence) {
            this.defaultDepartureAudioSequence = jsonData.defaultConfig.departureAudioSequence;
          }
          
          if (jsonData.defaultConfig.welcomeAudioSequence) {
            this.welcomeAudioSequence = jsonData.defaultConfig.welcomeAudioSequence;
          }
          
          if (jsonData.defaultConfig.terminalAudioSequence) {
            this.terminalAudioSequence = jsonData.defaultConfig.terminalAudioSequence;
          }
          
          // 导入上下行分别的配置
          if (jsonData.defaultConfig.separateDirections) {
            this.separateDirections = jsonData.defaultConfig.separateDirections;
          }
          
          if (jsonData.defaultConfig.upArrivalAudioSequence) {
            this.upArrivalAudioSequence = jsonData.defaultConfig.upArrivalAudioSequence;
          }
          
          if (jsonData.defaultConfig.downArrivalAudioSequence) {
            this.downArrivalAudioSequence = jsonData.defaultConfig.downArrivalAudioSequence;
          }
          
          if (jsonData.defaultConfig.upDepartureAudioSequence) {
            this.upDepartureAudioSequence = jsonData.defaultConfig.upDepartureAudioSequence;
          }
          
          if (jsonData.defaultConfig.downDepartureAudioSequence) {
            this.downDepartureAudioSequence = jsonData.defaultConfig.downDepartureAudioSequence;
          }
          
          if (jsonData.defaultConfig.upWelcomeAudioSequence) {
            this.upWelcomeAudioSequence = jsonData.defaultConfig.upWelcomeAudioSequence;
          }
          
          if (jsonData.defaultConfig.downWelcomeAudioSequence) {
            this.downWelcomeAudioSequence = jsonData.defaultConfig.downWelcomeAudioSequence;
          }
          
          if (jsonData.defaultConfig.upTerminalAudioSequence) {
            this.upTerminalAudioSequence = jsonData.defaultConfig.upTerminalAudioSequence;
          }
          
          if (jsonData.defaultConfig.downTerminalAudioSequence) {
            this.downTerminalAudioSequence = jsonData.defaultConfig.downTerminalAudioSequence;
          }
          
          // 导入服务语音频序列
          if (jsonData.defaultConfig.serviceAudioSequences) {
            this.serviceAudioSequences = jsonData.defaultConfig.serviceAudioSequences;
          }
        }
        
        // 导入多语言设置
        if (jsonData.languageSettings) {
          this.languageSettings = jsonData.languageSettings;
        }
        
        // 立即进行强制检测，基于导入的音频数据
        this.forceDetectSeparateDirections();
        
        // 使用nextTick确保在组件完全更新后同步状态
        this.$nextTick(() => {
          this.syncSeparateDirectionsToComponent();
          
          // 延迟验证导入后的状态
          setTimeout(() => {
            console.log('JSON导入后验证状态:');
            this.validateSeparateDirections();
          }, 300);
        });
        
        // 保存缓存
        this.saveCachedData();
        
        return true;
      } catch (error) {
        console.error('导入JSON数据失败:', error);
        alert('导入JSON数据失败: ' + error.message);
        return false;
      }
    },
    updateLanguageSettings(settings) {
      // 避免频繁更新导致输入框重置
      const currentSettings = JSON.stringify(this.languageSettings);
      const newSettings = JSON.stringify(settings);
      
      if (currentSettings !== newSettings) {
        this.languageSettings = JSON.parse(JSON.stringify(settings));
        console.log('语言设置已更新:', this.languageSettings);
        
        // 保存缓存
        this.saveCachedData();
        
        // 如果有打开的编辑模态框，通知其更新
        if (this.$refs.stationEditModal) {
          this.$refs.stationEditModal.$forceUpdate();
        }
      }
    },
    
    // 处理音频配置变化
    handleAudioConfigChanged(audioConfig) {
      console.log('📡 收到音频配置变化事件');
      
      // 更新音频播放列表数据
      this.defaultArrivalAudioSequence = audioConfig.defaultArrivalAudioSequence || [];
      this.defaultDepartureAudioSequence = audioConfig.defaultDepartureAudioSequence || [];
      this.welcomeAudioSequence = audioConfig.welcomeAudioSequence || [];
      this.terminalAudioSequence = audioConfig.terminalAudioSequence || [];
      this.upArrivalAudioSequence = audioConfig.upArrivalAudioSequence || [];
      this.downArrivalAudioSequence = audioConfig.downArrivalAudioSequence || [];
      this.upDepartureAudioSequence = audioConfig.upDepartureAudioSequence || [];
      this.downDepartureAudioSequence = audioConfig.downDepartureAudioSequence || [];
      this.upWelcomeAudioSequence = audioConfig.upWelcomeAudioSequence || [];
      this.downWelcomeAudioSequence = audioConfig.downWelcomeAudioSequence || [];
      this.upTerminalAudioSequence = audioConfig.upTerminalAudioSequence || [];
      this.downTerminalAudioSequence = audioConfig.downTerminalAudioSequence || [];
      this.serviceAudioSequences = audioConfig.serviceAudioSequences || Array(10).fill().map(() => []);
      this.separateDirections = audioConfig.separateDirections || {
        arrival: false,
        departure: false,
        welcome: false,
        terminal: false
      };
      
      console.log('✅ 音频配置已更新到App.vue:', {
        welcomeAudioSequence: this.welcomeAudioSequence,
        terminalAudioSequence: this.terminalAudioSequence,
        upWelcomeAudioSequence: this.upWelcomeAudioSequence,
        downWelcomeAudioSequence: this.downWelcomeAudioSequence,
        upTerminalAudioSequence: this.upTerminalAudioSequence,
        downTerminalAudioSequence: this.downTerminalAudioSequence
      });
      
      // 立即保存缓存
      this.saveCachedData();
      console.log('💾 缓存已保存');
    },
    updateLEDSettings(settings) {
      this.ledSettings = settings;
      console.log('LED设置已更新:', settings);
    },
    // 触发文件选择对话框
    triggerJsonFileInput() {
      document.getElementById('importJsonFile').click();
    },
    
    // 解析INI文件
    parseIniFile(content) {
      console.log('开始解析INI文件...');
      
      // 解析INI格式的辅助函数
      const parseIni = (text) => {
        const lines = text.split(/\r?\n/);
        const result = {};
        let currentSection = null;
        
        for (let line of lines) {
          line = line.trim();
          
          // 跳过空行和注释
          if (!line || line.startsWith(';') || line.startsWith('#')) continue;
          
          // 检测section
          const sectionMatch = line.match(/^\[([^\]]+)\]$/);
          if (sectionMatch) {
            currentSection = sectionMatch[1].trim();
            result[currentSection] = {};
            continue;
          }
          
          // 解析键值对
          if (currentSection) {
            const kvMatch = line.match(/^([^=]+)=(.*)$/);
            if (kvMatch) {
              const key = kvMatch[1].trim();
              const value = kvMatch[2].trim();
              result[currentSection][key] = value;
            }
          }
        }
        
        return result;
      };
      
      // 替换占位符
      const replacePlaceholders = (text) => {
        if (!text) return text;
        return text.replace(/【本站】/g, '【当前站】')
                   .replace(/【下站】/g, '【当前站】');
      };
      
      // 解析ini内容
      const iniData = parseIni(content);
      console.log('INI解析结果:', iniData);
      
      // 构建JSON数据结构
      const jsonData = {
        version: '1.0',
        lineName: '',
        lineType: 'twoWay',
        languageSettings: {
          enabled: false,
          count: 1,
          languages: [{ name: '中文', code: 'zh' }]
        },
        upDirectionStations: [],
        downDirectionStations: [],
        defaultConfig: {
          arrivalAudioSequence: [],
          departureAudioSequence: [],
          welcomeAudioSequence: [],
          terminalAudioSequence: [],
          separateDirections: {
            arrival: false,
            departure: false,
            welcome: false,
            terminal: false
          },
          upArrivalAudioSequence: [],
          downArrivalAudioSequence: [],
          upDepartureAudioSequence: [],
          downDepartureAudioSequence: [],
          upWelcomeAudioSequence: [],
          downWelcomeAudioSequence: [],
          upTerminalAudioSequence: [],
          downTerminalAudioSequence: [],
          serviceAudioSequences: Array(10).fill().map(() => []),
          arrivalDistance: 50,
          departureDistance: 50,
          ledSettings: {
            stationTextColor: '255',
            serviceTextColor: '65280',
            stationScrollMode: 'left',
            serviceScrollMode: 'left',
            stationNoScrollMode: 'left',
            serviceNoScrollMode: 'left',
            leftScrollSpeed: 10,
            upScrollSpeed: 5,
            arrivalLEDContent: '【当前站】 到了',
            departureLEDContent: '下一站 【当前站】'
          }
        }
      };
      
      // 1. 提取线路名
      if (iniData['线路'] && iniData['线路']['线路名']) {
        jsonData.lineName = iniData['线路']['线路名'];
        console.log('线路名:', jsonData.lineName);
      }
      
      // 2. 提取滚屏信息（LED显示内容）
      if (iniData['滚屏']) {
        const screenSection = iniData['滚屏'];
        
        // 默认滚屏 -> welcomeLEDContent（欢迎语LED显示内容）
        if (screenSection['默认滚屏']) {
          const text = replacePlaceholders(screenSection['默认滚屏']);
          jsonData.defaultConfig.ledSettings.welcomeLEDContent = text;
          console.log('欢迎语LED内容:', text);
        }
        
        // 到站滚屏 -> arrivalLEDContent（进站LED内容）
        if (screenSection['到站滚屏']) {
          const text = replacePlaceholders(screenSection['到站滚屏']);
          jsonData.defaultConfig.ledSettings.arrivalLEDContent = text;
          console.log('进站LED内容:', text);
        }
        
        // 出站滚屏 -> departureLEDContent（出站LED内容）
        if (screenSection['出站滚屏']) {
          const text = replacePlaceholders(screenSection['出站滚屏']);
          jsonData.defaultConfig.ledSettings.departureLEDContent = text;
          console.log('出站LED内容:', text);
        }
      }
      
      // 提取语音信息
      if (iniData['语音']) {
        const voiceSection = iniData['语音'];
        
        // 默认广播 -> 欢迎语
        if (voiceSection['默认广播']) {
          const text = replacePlaceholders(voiceSection['默认广播']);
          jsonData.defaultConfig.welcomeAudioSequence = [text];
          console.log('欢迎语:', text);
        }
        
        // 到站广播 -> 进站语
        if (voiceSection['到站广播']) {
          const text = replacePlaceholders(voiceSection['到站广播']);
          jsonData.defaultConfig.arrivalAudioSequence = [text];
          console.log('进站语:', text);
        }
        
        // 下一站广播 -> 出站语
        if (voiceSection['下一站广播']) {
          const text = replacePlaceholders(voiceSection['下一站广播']);
          jsonData.defaultConfig.departureAudioSequence = [text];
          console.log('出站语:', text);
        }
        
        // 终点站广播 -> 终点站语
        if (voiceSection['终点站广播']) {
          const text = replacePlaceholders(voiceSection['终点站广播']);
          jsonData.defaultConfig.terminalAudioSequence = [text];
          console.log('终点站语:', text);
        }
      }
      
      // 辅助函数：检查是否有有效的英文站名
      const hasValidEnglishNames = (englishStr) => {
        if (!englishStr) return false;
        const names = englishStr.split('|').map(s => s.trim()).filter(s => s.length > 0);
        return names.length > 0;
      };
      
      // 检查是否有英文站名
      let hasEnglish = false;
      let upEnglishStr = null;
      let downEnglishStr = null;
      
      if (iniData['上行'] && iniData['上行']['上行车站英文']) {
        upEnglishStr = iniData['上行']['上行车站英文'];
        if (hasValidEnglishNames(upEnglishStr)) {
          hasEnglish = true;
          console.log('检测到上行英文站名');
        }
      }
      
      if (iniData['下行'] && iniData['下行']['下行车站英文']) {
        downEnglishStr = iniData['下行']['下行车站英文'];
        if (hasValidEnglishNames(downEnglishStr)) {
          hasEnglish = true;
          console.log('检测到下行英文站名');
        }
      }
      
      // 如果有英文站名，启用多语言
      if (hasEnglish) {
        jsonData.languageSettings.enabled = true;
        jsonData.languageSettings.count = 2;
        jsonData.languageSettings.languages = [
          { name: '中文', code: 'zh' },
          { name: '英文', code: 'en' }
        ];
        console.log('已启用多语言模式（中英文）');
      }
      
      // 3. 提取上行车站
      // 尝试多个可能的字段名
      const upStationFields = ['上行车站', '途经车站', '车站', '站点'];
      let upStationsStr = null;
      
      if (iniData['上行']) {
        for (const field of upStationFields) {
          if (iniData['上行'][field]) {
            upStationsStr = iniData['上行'][field];
            console.log(`从[上行]读取字段"${field}":`, upStationsStr);
            break;
          }
        }
      }
      
      if (upStationsStr) {
        // 分割站点名称，过滤空字符串，并去除每个站点的首尾空格
        const stationNames = upStationsStr
          .split('|')
          .map(s => s.trim())
          .filter(s => s.length > 0);
        
        console.log('上行车站列表:', stationNames);
        
        // 处理英文站名
        let englishNames = [];
        if (upEnglishStr) {
          englishNames = upEnglishStr
            .split('|')
            .map(s => s.trim());
          console.log('上行英文站名列表:', englishNames);
        }
        
        jsonData.upDirectionStations = stationNames.map((name, index) => {
          const station = {
            id: index + 1,
            name: name,
            audioSequence: [],
            arrivalAudioSequence: [],
            departureAudioSequence: [],
            multiLanguageSequence: [],
            aliasSequence: [],
            latitude: 0,
            longitude: 0,
            arrivalDistance: 50,
            departureDistance: 50
          };
          
          // 如果有英文站名，添加到站点对象
          if (hasEnglish && englishNames[index] && englishNames[index].length > 0) {
            station.name_en = englishNames[index];
          }
          
          return station;
        });
        
        console.log('成功导入上行车站数量:', stationNames.length);
      } else {
        console.warn('未找到上行车站数据');
      }
      
      // 4. 提取下行车站
      const downStationFields = ['下行车站', '途经车站', '车站', '站点'];
      let downStationsStr = null;
      
      if (iniData['下行']) {
        for (const field of downStationFields) {
          if (iniData['下行'][field]) {
            downStationsStr = iniData['下行'][field];
            console.log(`从[下行]读取字段"${field}":`, downStationsStr);
            break;
          }
        }
      }
      
      if (downStationsStr) {
        // 分割站点名称，过滤空字符串，并去除每个站点的首尾空格
        const stationNames = downStationsStr
          .split('|')
          .map(s => s.trim())
          .filter(s => s.length > 0);
        
        console.log('下行车站列表:', stationNames);
        
        // 处理英文站名
        let englishNames = [];
        if (downEnglishStr) {
          englishNames = downEnglishStr
            .split('|')
            .map(s => s.trim());
          console.log('下行英文站名列表:', englishNames);
        }
        
        const upStationCount = jsonData.upDirectionStations.length;
        jsonData.downDirectionStations = stationNames.map((name, index) => {
          const station = {
            id: upStationCount + index + 1,
            name: name,
            audioSequence: [],
            arrivalAudioSequence: [],
            departureAudioSequence: [],
            multiLanguageSequence: [],
            aliasSequence: [],
            latitude: 0,
            longitude: 0,
            arrivalDistance: 50,
            departureDistance: 50
          };
          
          // 如果有英文站名，添加到站点对象
          if (hasEnglish && englishNames[index] && englishNames[index].length > 0) {
            station.name_en = englishNames[index];
          }
          
          return station;
        });
        
        console.log('成功导入下行车站数量:', stationNames.length);
      } else {
        console.warn('未找到下行车站数据');
      }
      
      console.log('INI解析完成，生成的JSON数据:', jsonData);
      return jsonData;
    },
    
    // 导入JSON文件
    async importJsonFileHandler(event) {
      const file = event.target.files[0];
      if (!file) return;
      await this.resetBeforeImport();
      
      const reader = new FileReader();
      const fileName = file.name.toLowerCase();
      reader.onload = (e) => {
        try {
          const fileContent = e.target.result;
          
          let jsonData;
          
          // 检测文件类型
          if (fileName.endsWith('.ini')) {
            // 解析INI文件
            jsonData = this.parseIniFile(fileContent);
          } else {
            // 解析JSON文件
            jsonData = JSON.parse(fileContent);
          }
          
          const success = this.importJsonData(jsonData);
          
          if (success) {
            // 清空文件输入，允许再次导入同一文件
            event.target.value = '';
            alert(fileName.endsWith('.ini') ? 'INI配置导入成功' : 'JSON配置导入成功');
          }
        } catch (error) {
          console.error('解析配置文件失败:', error);
          alert('解析配置文件失败: ' + error.message);
        }
      };
      
      reader.onerror = () => {
        alert('读取文件失败');
      };
      
      if (fileName.endsWith('.ini')) {
        // INI文件通常使用GB2312编码
        reader.readAsText(file, 'GB2312');
      } else {
        // 默认按UTF-8读取，例如导出的JSON配置
        reader.readAsText(file);
      }
    },

    // 获取使用到的提示语音频（不包括服务语音频）
    getUsedHintAudios() {
      const usedPaths = new Set();
      
      // 从默认序列中收集
      if (this.defaultArrivalAudioSequence) {
        this.defaultArrivalAudioSequence.forEach(item => {
          if (typeof item === 'string' && !item.startsWith('【') && 
             (item.includes('.') || item.includes('.'))) {
            usedPaths.add(item);
          }
        });
      }
      
      if (this.defaultDepartureAudioSequence) {
        this.defaultDepartureAudioSequence.forEach(item => {
          if (typeof item === 'string' && !item.startsWith('【') && 
             (item.includes('.') || item.includes('.'))) {
            usedPaths.add(item);
          }
        });
      }
      
      if (this.welcomeAudioSequence) {
        this.welcomeAudioSequence.forEach(item => {
          if (typeof item === 'string' && !item.startsWith('【') && 
             (item.includes('.') || item.includes('.'))) {
            usedPaths.add(item);
          }
        });
      }
      
      if (this.terminalAudioSequence) {
        this.terminalAudioSequence.forEach(item => {
          if (typeof item === 'string' && !item.startsWith('【') && 
             (item.includes('.') || item.includes('.'))) {
            usedPaths.add(item);
          }
        });
      }
      
      // 从站点自定义序列中收集
      const allStations = [...this.upDirectionStations, ...this.downDirectionStations];
      allStations.forEach(station => {
        if (station.audioSequence) {
          station.audioSequence.forEach(item => {
            if (typeof item === 'string' && !item.startsWith('【') && 
               (item.includes('.') || item.includes('.'))) {
              usedPaths.add(item);
            }
          });
        }
      });

      return Array.from(usedPaths).map(path => ({
        name: path.split('/').pop(),
        path: path
      }));
    },

    // 获取使用到的服务语音频
    getUsedServiceAudios() {
      const usedPaths = new Set();
      
      // 从服务语音频序列中收集
      if (this.serviceAudioSequences) {
        this.serviceAudioSequences.forEach(sequence => {
          if (sequence && Array.isArray(sequence)) {
            sequence.forEach(item => {
              if (typeof item === 'string' && !item.startsWith('【') && 
                 (item.includes('.') || item.includes('.'))) {
                usedPaths.add(item);
              }
            });
          }
        });
      }

      return Array.from(usedPaths).map(path => ({
        name: path.split('/').pop(),
        path: path
      }));
    },
    
    // 站点显示名称（简化版本）
    getStationDisplayName(station) {
      if (station.name) return station.name;
      if (this.languageSettings && this.languageSettings.languages) {
        for (let i = 0; i < this.languageSettings.languages.length; i++) {
          const code = this.languageSettings.languages[i].code;
          if (code && station['name_' + code]) {
            return station['name_' + code];
          }
        }
      }
      return '未命名';
    },
    
    // 站点拖拽排序相关方法
    onStationDragStart(event, index) {
      this.draggedStationIndex = index;
      event.dataTransfer.effectAllowed = 'move';
    },
    
    onStationDragOver(event) {
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
      this.updateStationDropIndicator(event);
    },
    
    onStationDragEnter(event) {
      event.preventDefault();
      this.updateStationDropIndicator(event);
    },
    
    onStationDragLeave(event) {
      // 只有当真正离开容器时才清除指示器
      if (!event.currentTarget.contains(event.relatedTarget)) {
        this.stationDropIndicatorIndex = null;
      }
    },
    
    updateStationDropIndicator(event) {
      const dropIndex = this.getStationDropIndex(event);
      this.stationDropIndicatorIndex = dropIndex;
    },
    
    getStationDropIndex(event) {
      const items = event.currentTarget.querySelectorAll('.station-item');
      const rect = event.currentTarget.getBoundingClientRect();
      const y = event.clientY - rect.top;
      
      for (let i = 0; i < items.length; i++) {
        const itemRect = items[i].getBoundingClientRect();
        const itemY = itemRect.top - rect.top + itemRect.height / 2;
        if (y < itemY) {
          return i;
        }
      }
      return items.length;
    },
    
    onStationDrop(event, dropIndex) {
      event.preventDefault();
      if (this.draggedStationIndex !== null && this.draggedStationIndex !== dropIndex) {
        this.moveStation(this.draggedStationIndex, dropIndex, this.currentDirection);
      }
      this.draggedStationIndex = null;
      this.stationDropIndicatorIndex = null;
    }
  }
};
</script>

<style>
/* 隐私政策模态框样式 */
.privacy-modal {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.5);
  z-index: 1060;
}
.privacy-modal-content {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: min(720px, 92vw);
  max-height: 80vh;
  overflow: auto;
  background: #fff;
  border-radius: 8px;
  padding: 16px 16px 12px 16px;
  box-shadow: 0 10px 30px rgba(0,0,0,.2);
}
.privacy-close {
  position: absolute;
  right: 8px;
  top: 6px;
  border: none;
  background: transparent;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
}
.privacy-body p {
  margin-bottom: 8px;
}
body {
  font-family: 'Microsoft YaHei', sans-serif;
  background-color: #f8f9fa;
}

/* body锁定（移动端抽屉打开时防滚动） */
.body--locked { overflow: hidden !important; }

.sidebar {
  background-color: #2c3e50;
  min-height: 100vh;
  padding: 20px;
}

.brand-header {
  height: 56px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(255,255,255,.15);
  margin-bottom: 10px;
}

.brand-logo {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  margin-right: 8px;
}

.brand-title {
  font-weight: 600;
  color: #ecf0f1;
  font-size: 14px;
}

/* 删除桌面折叠按钮样式（不再显示） */

/* 取消桌面折叠状态 */

.sidebar .nav-link {
  color: #ecf0f1;
  border-radius: 4px;
  padding: 10px 15px;
  transition: all 0.3s ease;
}

.sidebar .nav-link:hover {
  background-color: #34495e;
  color: #fff;
}

.sidebar .nav-link.active {
  background-color: #3498db;
  color: #fff;
}

.main-content {
  background-color: #fff;
  min-height: 100vh;
  padding: 20px;
  box-shadow: 0 0 10px rgba(0,0,0,0.1);
}

/* ===== 桌面端固定侧栏（≥768px） ===== */
@media (min-width: 768px) {
  .sidebar {
    position: sticky;
    top: 0;
    height: 100vh;
    align-self: flex-start;
    overflow: hidden;
  }
  .main-content {
    min-height: 100vh;
  }
}

.card {
  margin-bottom: 20px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
}

.station-list {
  height: 400px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 10px;
  background-color: white;
}

.station-item {
  padding: 8px 15px;
  border-bottom: 1px solid #eee;
  cursor: pointer;
}

.station-item:hover {
  background-color: #f1f8ff;
}

.station-item.active {
  background-color: #e7f3ff;
  border-left: 3px solid #007bff;
}

.audio-preview {
  margin-top: 10px;
  padding: 10px;
  background-color: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #dee2e6;
}

.audio-item {
  display: flex;
  align-items: center;
  padding: 5px 0;
}

.audio-item audio {
  width: 200px;
  height: 30px;
}

.template-editor {
  margin-top: 15px;
}

.placeholder-tag {
  display: inline-block;
  background-color: #e7f3ff;
  color: #0366d6;
  border-radius: 3px;
  padding: 2px 5px;
  margin: 0 3px;
  font-size: 0.9em;
  cursor: pointer;
}

/* 拖拽排序样式 */
.drag-handle {
  font-weight: bold;
  color: #6c757d;
  cursor: grab;
  user-select: none;
}

.drag-handle:active {
  cursor: grabbing;
}

.station-item[draggable="true"] {
  cursor: move;
}

.station-item[draggable="true"]:hover .drag-handle {
  color: #495057;
}

#stationListPersonalized .station-list {
  height: 400px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 10px;
  background-color: white;
}

#stationListPersonalized .station-item {
  padding: 8px 15px;
  border-bottom: 1px solid #eee;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

#stationListPersonalized .station-item:hover {
  background-color: #f1f8ff;
}

#stationListPersonalized .station-item.active {
  background-color: #e7f3ff;
  border-left: 3px solid #007bff;
}

#stationListPersonalized .badge-station-type {
  margin-left: 5px;
}

/* 站点拖拽辅助线样式 */
.drop-indicator {
  height: 2px;
  background-color: #000;
  margin: 4px 0;
  border-radius: 1px;
}

#stationListPersonalized .station-item.drag-over {
  border-top: 2px solid #000;
  margin-top: 2px;
}

#stationListPersonalized .station-item.drag-over .drag-handle {
  color: #000;
}

/* ===== 移动端适配：侧边栏折叠 ===== */
@media (max-width: 768px) {
  .mobile-topbar {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1065;
    height: 56px;
    background-color: #2c3e50;
    width: 100vw;
  }

  .brand-inline .brand-logo { width: 22px; height: 22px; border-radius: 6px; margin-right: 8px; }
  .brand-inline .brand-title { font-size: 14px; color: #ecf0f1; font-weight: 600; max-width: 62vw; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .hamburger-btn { border-color: rgba(255,255,255,.25); color: #ecf0f1; }

  .sidebar {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    max-width: 100vw;
    height: 100vh;
    transform: translateX(-100%);
    transition: transform .25s ease;
    z-index: 1050;
    box-shadow: 2px 0 12px rgba(0,0,0,.25);
  }
  .sidebar.mobile-open {
    display: block !important;
    transform: translateX(0);
  }
  .mobile-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,.4);
    z-index: 1045;
  }
  .main-content {
    padding-top: 12px;
    min-height: calc(100vh - 56px);
  }

  /* remove container paddings/margins that cause horizontal overflow on small screens */
  .container-fluid { padding-left: 0; padding-right: 0; padding-top: 56px; overflow-x: hidden; }
  .row { margin-left: 0; margin-right: 0; }
  html, body { overflow-x: hidden; }
}
</style> 